import actionTypes from './formEditor.actionTypes';
import { getQuestionCount, removeHtmlTags, decodeText } from '../Common/Services/utilities';
import { TalentFormFormTypes } from '../formViewer/formViewer.constants';
import * as JsonConverter from '../Common/Services/surveyJsConverters';
import moment from 'moment';


const initialState = {
  companyId: '',
  employeeId: '',
  isLoading: false,
  isQuestionBankLoading: false,
  isSavingTemplate: false,
  isFormValid: true,
  isFormModified: false,
  isNewTemplate: false,
  isCopyAction: false,
  isViewAction: false,
  formTypeId: 0,
  createdByName: '',
  lastAutoSave: null,
  formQuestionsValid: true,
  form: {
    templateName: '',
    configuration: '',
    isTemplateOwner: false,
    isDefaultTemplate: false,
    formCategoryTypeId: '',
    formTypeId: 0,
    launchCount: 0
  },
  questionBankQuestions: [],
};

const validateForm = (state) => {
  let form = state.form;

  const isCategoryRequired = (state.formTypeId === TalentFormFormTypes.Surveys);
  const isCategoryValid = isCategoryRequired ? (form.formCategoryTypeId !== "") : true;
  const isNameValid = (form.templateName !== "");
  const contentItemsCount = getQuestionCount(form.configuration, false, true);
  const isConfigurationValid = (form.configuration !== "" && contentItemsCount > 0);

  const valid = (isNameValid && isCategoryValid && isConfigurationValid && state.formQuestionsValid);
  return valid;
}

export default function (state = initialState, action) {
  switch (action.type) {
    case actionTypes.FORM_EDITOR_FIELD_CHANGED: return formEditorFieldChanged(state, action);
    case actionTypes.FORM_EDITOR_CONFIGURATION_CHANGED: return formEditorConfigurationChanged(state, action);
    case actionTypes.FORM_EDITOR_QUESTIONS_VALID_CHANGED: return formEditorQuestionsValidChanged(state, action);
    case actionTypes.CREATE_FORM_TEMPLATE: return createFormTemplate(state, action);

    case actionTypes.LOAD_FORM_TEMPLATE_BEGIN: return loadFormTemplateBegin(state, action);
    case actionTypes.LOAD_FORM_TEMPLATE_SUCCESS: return loadFormTemplateSuccess(state, action);
    case actionTypes.LOAD_FORM_TEMPLATE_FAILURE: return loadFormTemplateFailure(state);

    case actionTypes.SAVE_FORM_TEMPLATE_BEGIN: return saveFormTemplateBegin(state);
    case actionTypes.SAVE_FORM_TEMPLATE_SUCCESS: return saveFormTemplateSuccess(state, action);
    case actionTypes.SAVE_FORM_TEMPLATE_FAILURE: return saveFormTemplateFailure(state);

    case actionTypes.LOAD_QUESTION_BANK_BEGIN: return loadQuestionBankBegin(state, action);
    case actionTypes.LOAD_QUESTION_BANK_SUCCESS: return loadQuestionBankSuccess(state, action);
    case actionTypes.LOAD_QUESTION_BANK_FAILURE: return loadQuestionBankFailure(state);

    case actionTypes.RESET_FORM_EDITOR_STATE: return resetState();

    default:
      return state;
  }
}

const createFormTemplate = (state, { payload }) => {
  return {
    ...initialState,
    isFormValid: false,
    isFormModified: true,
    isNewTemplate: true,
    companyId: payload.companyId,
    employeeId: payload.employeeId,
    formTypeId: payload.formTypeId,
    createdByName: payload.createdByName,
    form: {
      ...initialState.form,
      templateName: payload.wizardFormName ? payload.wizardFormName : '',
      formCategoryTypeId: payload.wizardFormCategoryType ? payload.wizardFormCategoryType.value : null,
      isTemplateOwner: true
    }
  }
};

const formEditorFieldChanged = (state, { payload }) => {
  const pendingState = {
    ...state,
    isFormModified: true,
    form: {
      ...state.form,
      [payload.field]: payload.value
    }
  };

  const isFormValid = validateForm(pendingState);
  return {
    ...pendingState,
    isFormValid: isFormValid,
  }
};

const formEditorQuestionsValidChanged = (state, { payload }) => {
  const pendingState = {
    ...state,
    isFormModified: true,
    formQuestionsValid: payload.valid
  };

  const isFormValid = validateForm(pendingState);
  return {
    ...pendingState,
    isFormValid: isFormValid,
  }
};

const formEditorConfigurationChanged = (state, { payload }) => {
  const questionCount = getQuestionCount(payload.configuration);

  const pendingState = {
    ...state,
    isFormModified: true,
    form: {
      ...state.form,
      configuration: payload.configuration,
      questionCount: questionCount,
    }
  };

  const isFormValid = validateForm(pendingState);
  return {
    ...pendingState,
    isFormValid: isFormValid,
  }
};

const loadFormTemplateBegin = (state, { payload }) => {
  return {
    ...state,
    isLoading: true,
    isFormModified: false,
    companyId: payload.companyId,
    employeeId: payload.employeeId,
    createdByName: payload.createdByName,
  };
};

const loadFormTemplateSuccess = (state, action) => {
  let form = action.data.form;
  let formCategoryTypeId = form.formCategoryTypeId ? form.formCategoryTypeId.toString() : '';

  return {
    ...state,
    isLoading: false,
    isNewTemplate: false,
    //isCompanyAdministrator: action.data.isCompanyAdministrator,
    formTypeId: form.formTypeId,
    formQuestionsValid: true,
    form: {
      ...form,
      formCategoryTypeId: formCategoryTypeId
    },
    isCopyAction: action.data.copy,
    isViewAction: action.data.view,
  };
};

const loadFormTemplateFailure = state => {
  return {
    ...state,
    isLoading: false
  };
};

const saveFormTemplateBegin = state => {
  return {
    ...state,
    form: {
      ...state.form,
    },
    isSavingTemplate: true
  };
};

const saveFormTemplateSuccess = (state, action) => {
  let time = moment();
  let lastSavedTime = state.lastAutoSave
    ? (moment(time).isAfter(state.lastAutoSave) ? time : state.lastAutoSave)
    : time;

  return {
    ...state,
    lastAutoSave: lastSavedTime,
    isSavingTemplate: false,
    isFormModified: false,
    form: { ...action.data }
  };
};

const saveFormTemplateFailure = state => {
  return {
    ...state,
    isSavingTemplate: false
  };
};

const resetState = () => {
  return { ...initialState };
};



const loadQuestionBankBegin = state => {
  return {
    ...state,
    isQuestionBankLoading: true
  };
};

const loadQuestionBankSuccess = (state, action) => {
  let questions = action.data.questions;

  // elements are surveyJS, so convert to citrus and update the elements to have both!
  const convertedQuestionBankQuestions = questions.map((qbd) => {
    const surveyElement = JSON.parse(qbd.questionConfiguration);
    qbd.citrusElement = JsonConverter.convertSurveyjsToCitrusJsonQuestion(surveyElement);
    qbd.questionText = removeHtmlTags(decodeText(qbd.citrusElement.question));
    return qbd;
  });

  return {
    ...state,
    isQuestionBankLoading: false,
    questionBankQuestions: convertedQuestionBankQuestions,
  };
};

const loadQuestionBankFailure = state => {
  return {
    ...state,
    isQuestionBankLoading: false
  };
};


